// src/common/DesktopNavbar.tsx
import React, { useEffect, useState, useRef } from "react";
import logo from "../assets/logo.svg";
import { Link, useLocation } from "react-router-dom";
import { NAVBAR } from "../data/constants";
import EnquiryModal from "./EnquiryModal";
import { motion, AnimatePresence } from "framer-motion";

interface DesktopNavbarProps {
  onOpenEnquireModal: () => void;
}

const DesktopNavbar: React.FC<DesktopNavbarProps> = () => {
  /* ───────── state ───────── */
  const [scrolled, setScrolled]         = useState(false);
  const [showMega, setShowMega]         = useState(false);
  const [isModalOpen, setIsModalOpen]   = useState(false);
  const menuTimeout                     = useRef<number | null>(null);
  const menuContentRef                  = useRef<HTMLDivElement>(null);
  const [menuH, setMenuH]               = useState(0);
  const location                        = useLocation();

  /* ───────── effects ───────── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (showMega && menuContentRef.current)
      setMenuH(menuContentRef.current.scrollHeight);
  }, [showMega]);

  useEffect(() => window.scrollTo({ top: 0, behavior: "smooth" }), [location]);

  /* ───────── dynamic classes ───────── */
  const glass = scrolled
    ? "bg-[#07394a] backdrop-blur-md"
    : "bg-black/10 backdrop-blur-lg";

  const buttonClass = [
    "inline-flex items-center justify-center w-[125px] h-[46px]",
    "px-8 pt-[15px] pb-[15px] gap-[12px] bg-white text-black font-semibold",
    "shadow-md overflow-hidden transition-all duration-100",
    "[clip-path:polygon(0_0,100%_0,100%_100%,0_100%)]",
    "hover:bg-[#46C1BF]",
    "hover:[clip-path:polygon(0_0,100%_0,100%_calc(100%_-_16px),calc(100%_-_16px)_100%,0_100%)]"
  ].join(" ");

  /* ───────── menu handlers ───────── */
  const open  = () => { if (menuTimeout.current) clearTimeout(menuTimeout.current); setShowMega(true); };
  const close = () =>  menuTimeout.current = window.setTimeout(() => setShowMega(false), 100);

  /* ───────── JSX ───────── */
  return (
    <>
      <header className={`fixed inset-x-0 top-0 z-50 isolate transition-colors duration-300 ${glass}`}>
        {/* navbar row */}
        <div className="flex items-center justify-between h-20 px-6 md:px-20">
          <Link to="/"><img src={logo} alt="SKTuff Logo" className="h-10 w-auto" /></Link>

          <nav className="hidden md:flex space-x-10 text-white font-medium ">
            {NAVBAR.navLinks.map(item =>
              item.label === "Products" ? (
                <div
                  key={item.to}
                  className="relative"
                  onMouseEnter={open}
                  onMouseLeave={close}
                  onFocus={open}
                  onBlur={close}
                  tabIndex={0}
                >
                  <span className={`cursor-default transition-colors ${showMega ? "border-b-2" : ""}`}>{item.label}</span>
                </div>
              ) : (
                <Link key={item.to} to={item.to} className="relative inline-block group">
                  {item.label}
                  <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-white transition-all group-hover:w-full" />
                </Link>
              )
            )}
          </nav>

          <button className={buttonClass} onClick={() => setIsModalOpen(true)}>{NAVBAR.enquireButton}</button>
        </div>

        {/* mega-menu (same container) */}
        <AnimatePresence>
          {showMega && (
            <motion.div
              className="w-full overflow-hidden"
              onMouseEnter={open}
              onMouseLeave={close}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: menuH, opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            >
              <div ref={menuContentRef} className="relative">
                <div className="mx-auto flex max-w-7xl px-12 py-8 gap-16">
                  <div className="pr-12">
                    <img src={NAVBAR.megaMenu.image} alt="Glass" className="w-[294px] h-[154px] rounded object-cover shadow" />
                  </div>
                  <div className="flex flex-1 gap-16">
                    {NAVBAR.megaMenu.categories.map(cat => (
                      <div key={cat.title}>
                        <Link
                          to={cat.link}
                          className="font-semibold text-white text-base uppercase"
                          onClick={() => setShowMega(false)}
                        >
                          {cat.title}
                        </Link>
                        <ul className="mt-2 space-y-1">
                          {cat.links.map(link => (
                            <li key={link.to}>
                              <Link
                                to={link.to}
                                className="text-sm text-white hover:text-[#46C1BF] transition"
                                onClick={() => setShowMega(false)}
                              >
                                {link.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* modal */}
      <EnquiryModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default DesktopNavbar;
